import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import AuthProvider from './context/AuthContext';
import BrandProvider from './context/BrandContext';
import Navigation from './components/ui/Navigation';
import SoftGateBanner from './components/SoftGateBanner';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import BrandGuard from './components/BrandGuard';
import Toaster from './components/Toaster';
import Homepage from './pages/Homepage';
import StreamCreator from './pages/StreamCreator';
import ProfileHub from './pages/ProfileHub';
import Login from './pages/Login';
import Users from './pages/Users';
import AdminMetrics from './pages/AdminMetrics';
import UploadsAdmin from './pages/UploadsAdmin';
import MyUploads from './pages/MyUploads';
import Discover from './pages/Discover';
import Amplification from './pages/Amplification';
import Register from './pages/Register';
import UserDetail from './pages/UserDetail';
import AdminHub from './pages/AdminHub';
import AdminStreamsModeration from './pages/AdminStreamsModeration';
import AdminSubscriptions from './pages/AdminSubscriptions';
import AdminSettings from './pages/AdminSettings';
import AdStudio from './pages/AdStudio';
import AdComposer from './pages/AdComposer';
import AdminDemoAds from './pages/AdminDemoAds';
import SystemAnalysis from './pages/SystemAnalysis';
import Onboarding from './pages/Onboarding';
import Transcribe from './pages/Transcribe';
import StreamDetail from './pages/StreamDetail';
import AdView from './pages/AdView';
import ProjectManager from './pages/ProjectManager';
import ProjectDetails from './pages/ProjectDetails';
import ImageCreator from './pages/ImageCreator';
import VideoCreator from './pages/VideoCreator';
import AudioCreator from './pages/AudioCreator';
import QuickStart from './pages/QuickStart';
import BrandWorkspace from './pages/BrandWorkspace';
import api from './services/api';
import ErrorBoundary from './components/ErrorBoundary';
function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    try {
      // Generate or reuse anonymous ID
      let anonId = localStorage.getItem('anon_id');
      if (!anonId) {
        const rand = Math.random().toString(36).slice(2);
        const ts = Date.now().toString(36);
        anonId = `anon_${ts}_${rand}`;
        localStorage.setItem('anon_id', anonId);
      }
      const path = location.pathname + (location.search || '');
      const referrer = document.referrer || '';
      // Non-blocking analytics call
      api.trackPageview({ path, referrer, anonId }).catch(() => {});
    } catch {}
  }, [location.pathname, location.search]);
  return null;
}

export default function App() {
  return (
    <AuthProvider>
      <BrandProvider>
        {/* This data-theme="dark" is inert: styles/tokens.css's [data-theme="dark"]
            rule is empty (inherits :root), so it declares no custom properties
            and doesn't block ProfileHub's theme picker, which sets data-theme
            on <html> and cascades down regardless of this attribute. */}
        <div className="app-shell" data-theme="dark">
          <a href="#main-content" className="skip-link">Skip to content</a>
          <Navigation />
          <RouteTracker />
          <SoftGateBanner />
          <main className="app-main" id="main-content">
          <Routes>
          <Route path="/" element={<ErrorBoundary><Homepage /></ErrorBoundary>} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/discover" element={<ErrorBoundary><Discover /></ErrorBoundary>} />
          <Route path="/streams/:id" element={<ErrorBoundary><StreamDetail /></ErrorBoundary>} />
          <Route path="/ads/:id" element={<AdView />} />
          {/* Primary wedge: create a voice ad (no brand gate) */}
          <Route path="/create" element={<ErrorBoundary><StreamCreator /></ErrorBoundary>} />
          <Route path="/stream" element={<ErrorBoundary><StreamCreator /></ErrorBoundary>} />
          {/* Legacy surfaces: still reachable by URL / admin; brand-gated where heavy */}
          <Route
            path="/projects"
            element={<ProtectedRoute><BrandGuard><ProjectManager /></BrandGuard></ProtectedRoute>}
          />
          <Route
            path="/projects/:id"
            element={<ProtectedRoute><BrandGuard><ErrorBoundary><ProjectDetails /></ErrorBoundary></BrandGuard></ProtectedRoute>}
          />
          <Route
            path="/quickstart"
            element={<ProtectedRoute><BrandGuard><ErrorBoundary><QuickStart /></ErrorBoundary></BrandGuard></ProtectedRoute>}
          />
          <Route
            path="/brand"
            element={<ProtectedRoute><ErrorBoundary><BrandWorkspace /></ErrorBoundary></ProtectedRoute>}
          />
          <Route path="/audio" element={<AudioCreator />} />
          <Route path="/images" element={<ImageCreator />} />
          <Route path="/videos" element={<ErrorBoundary><VideoCreator /></ErrorBoundary>} />
          <Route path="/amplify" element={<ProtectedRoute><Amplification /></ProtectedRoute>} />
          <Route path="/adstudio" element={<ProtectedRoute><AdStudio /></ProtectedRoute>} />
          <Route path="/adcomposer" element={<ProtectedRoute><AdComposer /></ProtectedRoute>} />
          <Route path="/transcribe" element={<ProtectedRoute><Transcribe /></ProtectedRoute>} />
          <Route
            path="/profile"
            element={<ProtectedRoute><ErrorBoundary><ProfileHub /></ErrorBoundary></ProtectedRoute>}
          />
          <Route
            path="/users/:id"
            element={<AdminRoute><UserDetail /></AdminRoute>}
          />
          <Route
            path="/admin"
            element={<AdminRoute><AdminHub /></AdminRoute>}
          />
          <Route
            path="/admin/streams-moderation"
            element={<AdminRoute><AdminStreamsModeration /></AdminRoute>}
          />
          <Route
            path="/admin/demo-ads"
            element={<AdminRoute><AdminDemoAds /></AdminRoute>}
          />
          <Route
            path="/admin/onboarding"
            element={<AdminRoute><Onboarding /></AdminRoute>}
          />
          <Route
            path="/admin/uploads"
            element={<AdminRoute><UploadsAdmin /></AdminRoute>}
          />
          <Route
            path="/admin/metrics"
            element={<AdminRoute><AdminMetrics /></AdminRoute>}
          />
          <Route
            path="/admin/subscriptions"
            element={<AdminRoute><AdminSubscriptions /></AdminRoute>}
          />
          <Route
            path="/admin/settings"
            element={<AdminRoute><AdminSettings /></AdminRoute>}
          />
          <Route
            path="/admin/system"
            element={<AdminRoute><SystemAnalysis /></AdminRoute>}
          />
          <Route
            path="/uploads"
            element={<ProtectedRoute><MyUploads /></ProtectedRoute>}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
          </main>
        <Toaster />
      </div>
      </BrandProvider>
    </AuthProvider>
  );
}
